import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ROUTES } from 'Utils/constants';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useNavbarItems from 'Hooks/useNavbarItems';
import navLogoOpen from 'assets/nav-logo-open.png';
import navLogoClosed from 'assets/nav-logo-closed.png';
import IconButton from 'Components/Atoms/IconButtonBare';
import useSettingsStorage from 'Hooks/useSettingsStorage';
import Button from 'Components/Atoms/Button/Button';
import AppLayoutSidebar from './AppLayoutSidebar';
import OutlineNav from 'Components/Atoms/OutlineIcons/OutlineNav';

export default function LayoutSidebarDesktop({ collapsed }) {
    const [editing, setEditing] = useState(false);
    const { settings, setSetting } = useSettingsStorage();
    const isMenuCollapsed = settings?.is_menu_collapsed?.value;

    const { t } = useTranslation('common');

    const { onSave, onCancel } = useNavbarItems();

    const handleTogglePinnedMenu = () => {
        setSetting({
            field: 'is_menu_collapsed',
            value: !isMenuCollapsed,
            is_global: true,
        });
    };

    const handleSave = () => {
        setEditing(false);
        onSave();
    };

    const handleToggleEdit = () => {
        if (editing) {
            onCancel();
        }

        setEditing(!editing);
    };

    useEffect(() => {
        if (collapsed && editing) {
            setEditing(false);
            onCancel();
        }
    }, [collapsed, editing]);

    return (
        <div className="w-(--sidebar-width) h-full grid grid-rows-[auto_1fr_auto] group">
            <div className="pt-6 px-4.5 min-h-[98px] shrink-0 relative">
                <Link to={ROUTES.dashboards}>
                    <img src={collapsed ? navLogoClosed : navLogoOpen} alt="Banktrack" height={28} width={136} />
                </Link>

                <div className="absolute top-7 right-3 flex items-center gap-1 opacity-0 [@media(hover:none)]:opacity-100 group-hover:opacity-100 transition-opacity">
                    <IconButton onClick={handleTogglePinnedMenu} data-testid="nav.sidebar-pin-toggle">
                        <svg width="13" height="15" viewBox="0 0 13 15" xmlns="http://www.w3.org/2000/svg">
                            <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
                                <g transform="translate(-23 -33)">
                                    <g transform="translate(24.217 34)">
                                        <path
                                            stroke="rgba(32,32,32, .5)"
                                            strokeLinejoin="round"
                                            strokeWidth="1.5"
                                            d="M8.783 0a1 1 0 011 1v1a1 1 0 01-1 1h-1v3c1.174.587 2.09 1.69 2.747 3.31a.5.5 0 01-.463.69H.5a.5.5 0 01-.463-.69C.694 7.69 1.61 6.588 2.783 6V3h-1a1 1 0 01-1-1V1a1 1 0 011-1h7z"
                                        ></path>
                                        <rect
                                            width="1.5"
                                            height="4"
                                            x="4.533"
                                            y="10"
                                            fill="rgba(32,32,32, .5)"
                                            rx="0.75"
                                        ></rect>
                                    </g>
                                </g>
                            </g>
                        </svg>
                    </IconButton>
                </div>
            </div>

            <div className="pb-8 px-2.5">
                <AppLayoutSidebar collapsed={collapsed} editing={editing} />
            </div>

            <div className="px-3 pb-4 flex flex-col items-center">
                {editing && (
                    <div className="grid gap-2 w-full">
                        <Button size="small" variant="primary" onClick={handleSave} testId="nav.sidebar-save">
                            {t('save')}
                        </Button>
                        <Button size="small" onClick={handleToggleEdit} testId="nav.sidebar-cancel">
                            {t('cancel')}
                        </Button>
                    </div>
                )}

                {!editing && (
                    <Button
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        size="small"
                        variant="ghost"
                        onClick={handleToggleEdit}
                        testId="nav.sidebar-edit"
                    >
                        <OutlineNav strokeWidth={1.7} size={18} color="currentColor" />
                        <span>{t('edit-navigation')}</span>
                    </Button>
                )}
            </div>
        </div>
    );
}

LayoutSidebarDesktop.propTypes = {
    onClose: PropTypes.func.isRequired,
    collapsed: PropTypes.bool,
    className: PropTypes.string,
};
